/**
 * Types for GET /widget/verify/ (OpenAPI: `/api/v1/widget/verify/`,
 * schema `WidgetVerifyResponse`). Call before loading the survey for config and allow/deny.
 */

/**
 * Known `reason` values when `allowed` is false; the API may return other strings.
 */
export type ValidateSurveyReason =
  | "invalid_request"
  | "project_not_found"
  | "unpublished"
  | "host_not_allowed";

/**
 * JSON body from GET /widget/verify/. Matches OpenAPI `components/schemas/WidgetVerifyResponse`.
 */
export interface WidgetVerifyResponse {
  allowed: boolean;
  reason?: string | null;
  already_completed?: boolean;
  response_id?: string;
  interview_status?: string;
  current_question_number?: number;
  /** Required in the schema; nullable. */
  widget_color: string | null;
  /** Required in the schema; nullable. */
  widget_message_text: string | null;
  /** Required in the schema; nullable. Trigger placement from the API. */
  position: string | null;
  /** Required in the schema; nullable. */
  trigger_type: string | null;
}

/** Alias for {@link WidgetVerifyResponse} (existing import name). */
export type ValidateSurveyResult = WidgetVerifyResponse;

export type ValidateSurveySuccess = WidgetVerifyResponse & { allowed: true };

export type ValidateSurveyFailure = WidgetVerifyResponse & { allowed: false };

/** Type guard: true if validation result indicates the survey is allowed. */
export function isValidateSurveySuccess(
  result: WidgetVerifyResponse
): result is ValidateSurveySuccess {
  return result.allowed === true;
}

export type VerifyTriggerType = "floating" | "tab";
export type VerifyTriggerPosition =
  | "bottom-left"
  | "bottom-right"
  | "top-left"
  | "top-right"
  | "left"
  | "right";

function normalizeVerifyValue(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  return normalized ? normalized : null;
}

function getVerifyString(
  payload: unknown,
  keys: string[]
): string | null {
  if (!payload || typeof payload !== "object") return null;
  const record = payload as Record<string, unknown>;
  for (const key of keys) {
    const normalized = normalizeVerifyValue(record[key]);
    if (normalized) return normalized;
  }
  return null;
}

/**
 * Parse trigger type from verify payload. Returns null when missing or unsupported.
 */
export function getVerifyTriggerType(payload: unknown): VerifyTriggerType | null {
  const raw = getVerifyString(payload, [
    "trigger_type",
    "triggerType",
    "widget_trigger_type",
    "widgetTriggerType",
  ]);
  if (!raw) return null;
  if (raw === "tab" || raw === "side-tab" || raw === "side_tab") return "tab";
  if (raw === "floating" || raw === "button" || raw === "mic" || raw === "microphone") {
    return "floating";
  }
  return null;
}

/**
 * Parse trigger position from verify payload. Returns null when missing or unsupported.
 * OpenAPI uses `position`; legacy keys are still accepted.
 */
export function getVerifyTriggerPosition(
  payload: unknown
): VerifyTriggerPosition | null {
  const raw = getVerifyString(payload, [
    "position",
    "trigger_position",
    "triggerPosition",
    "widget_trigger_position",
    "widgetTriggerPosition",
  ]);
  if (!raw) return null;
  if (raw === "bottom-left" || raw === "bottom_left" || raw === "bottomleft") {
    return "bottom-left";
  }
  if (raw === "bottom-right" || raw === "bottom_right" || raw === "bottomright") {
    return "bottom-right";
  }
  if (raw === "top-left" || raw === "top_left" || raw === "topleft") {
    return "top-left";
  }
  if (raw === "top-right" || raw === "top_right" || raw === "topright") {
    return "top-right";
  }
  if (raw === "left" || raw === "right") return raw;
  return null;
}
